import { useQuery } from '@apollo/client/react';
import { FolderOpen, Pencil, Trash2 } from 'lucide-react';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeletePerformanceResultMutation, FetchPerformanceResults } from '../shared/PerformanceResults.graphql';
import useListReducer, { pageSizeOptions } from '../shared/useListReducer';
import { type AssessmentRunOption, type PerformanceFilterState, type PerformanceTabRef } from '../shared/utils';
import EditPerformanceMarch20KmModal from './Edit/EditPerformanceMarch20KmModal';
import client from '@/apolloClient';
import useAppAbilities from '@/components/abilities';
import Empty from '@/components/blocks/Empty';
import EmptyContainer from '@/components/common/EmptyContainer';
import IconButton from '@/components/common/IconButton';
import Loader from '@/components/common/Loader';
import Table from '@/components/common/Table';
import TableFooter from '@/components/common/TableFooter';
import { useCompany } from '@/contexts/UserSession';
import { PerformanceResultType, UserRole } from '@/gql/enums';
import type { PassFailResult } from '@/gql/enums';
import { confirm } from '@/utils/ConfirmModal';
import { formatSecondsAsHoursMin } from '@/utils/common';
import { success as showSuccessNotification, error as showErrorNotification } from '@/utils/notifications/actions';
import useCompanyRoleLabels from '@/utils/useCompanyRoleLabels';
import useDateFormats from '@/utils/useDateFormats';

export interface March20kmPerformanceRowData {
    id: string;
    traineeName: string;
    assessmentDate?: Date;
    timingSec?: number;
    result?: PassFailResult;
    assessmentRunId?: string;
    isTiedToAssessmentRun?: boolean;
    assessmentRunName?: string;
    lastActionBy?: string;
    lastUpdated?: Date;
}

interface March20kmPerformanceTabProps {
    filterState: PerformanceFilterState;
    assessmentRunOptions: AssessmentRunOption[];
}

const March20kmPerformanceTab = forwardRef<PerformanceTabRef, March20kmPerformanceTabProps>(
    ({ filterState, assessmentRunOptions }, ref) => {
        const { t } = useTranslation('performanceResult');
        const { formatDate, formatDateTimeWithSeconds } = useDateFormats();
        const { companyId: selectedCompanyId } = useCompany(true);
        const { getRoleLabel } = useCompanyRoleLabels();
        const abilities = useAppAbilities();

        const { filterAssessmentRunIds, traineeName, groupId, subGroupId } = filterState;

        const [state, dispatch] = useListReducer();
        const { page, pageSize } = state;

        const [openEditModal, setOpenEditModal] = useState(false);
        const [editData, setEditData] = useState<March20kmPerformanceRowData | null>(null);

        const filter = useMemo(
            () => ({
                assessmentRunIds: filterAssessmentRunIds.length > 0 ? filterAssessmentRunIds : null,
                searchText: traineeName ?? undefined,
                resultTypes: [PerformanceResultType.March20km],
                companyIds: selectedCompanyId ? [selectedCompanyId] : null,
                groupIds: groupId ? [groupId] : null,
                subgroupIds: subGroupId ? [subGroupId] : null,
            }),
            [filterAssessmentRunIds, traineeName, selectedCompanyId, groupId, subGroupId]
        );

        const { loading, data, error, refetch } = useQuery(FetchPerformanceResults, {
            fetchPolicy: 'cache-and-network',
            variables: {
                pagination: {
                    limit: pageSize,
                    offset: (page - 1) * pageSize,
                },
                filter,
            },
        });

        useImperativeHandle(
            ref,
            () => ({
                refetch,
            }),
            [refetch]
        );

        const totalCount = useMemo(() => data?.performanceResults?.count || 0, [data]);

        const handleEdit = useCallback((row: March20kmPerformanceRowData) => {
            setOpenEditModal(true);
            setEditData(row);
        }, []);

        const handleDelete = useCallback(
            (record: March20kmPerformanceRowData) => {
                confirm(
                    {
                        title: t('actions.deleteDetail.title'),
                        content: t('actions.deleteDetail.message'),
                        isDeletion: true,
                    },
                    async () => {
                        try {
                            const { data } = await client.mutate({
                                mutation: DeletePerformanceResultMutation,
                                variables: { id: record.id },
                            });

                            if (data?.deletePerformanceResult?.__typename === 'ErrorResponse') {
                                const errorResponse = data.deletePerformanceResult;
                                const specificErrorField = errorResponse.fields?.find(f => f.message);
                                const errorMessage =
                                    specificErrorField?.message ||
                                    t('deleteError.description', { type: '20km Combat March' });

                                showErrorNotification({
                                    message: t('deleteError.title', { type: '20km Combat March' }),
                                    description: errorMessage,
                                });

                                throw new Error(errorMessage);
                            } else {
                                showSuccessNotification({
                                    message: t('deleteSuccess.title', { type: '20km Combat March' }),
                                    description: t('deleteSuccess.message', { type: '20km Combat March' }),
                                });

                                refetch();
                            }
                        } catch (error) {
                            console.error('Error deleting 20km Combat March performance result:', error);
                            throw error;
                        }
                    }
                );
                return true;
            },
            [t, refetch]
        );

        const handleOnEditModalClose = useCallback(
            (refresh?: boolean) => {
                setOpenEditModal(false);
                setEditData(null);

                if (refresh) {
                    refetch();
                }
            },
            [refetch]
        );

        const columns = useMemo(
            () => [
                {
                    title: t('march20km.table.traineeName', { role: getRoleLabel(UserRole.Trainee) }),
                    dataIndex: 'traineeName',
                    key: 'traineeName',
                    className: 'font-semibold',
                },
                {
                    title: t('march20km.table.assessmentDate'),
                    dataIndex: 'assessmentDate',
                    key: 'assessmentDate',
                    render: (value: Date | null | undefined) => {
                        if (!value) {
                            return t('emptyValue');
                        }
                        return formatDate(value);
                    },
                },
                {
                    title: t('march20km.table.timingSec'),
                    dataIndex: 'timingSec',
                    key: 'timingSec',
                    render: (value: number | null | undefined) => {
                        const formatted = formatSecondsAsHoursMin(value ?? undefined);
                        return formatted ?? t('emptyValue');
                    },
                },
                {
                    title: t('march20km.table.result'),
                    dataIndex: 'result',
                    key: 'result',
                    render: (value: PassFailResult | null | undefined) => value ?? t('emptyValue'),
                },
                {
                    title: t('filter.assessmentRun'),
                    dataIndex: 'assessmentRunName',
                    key: 'assessmentRunName',
                    render: (value: string | null | undefined) => value ?? t('emptyValue'),
                },
                {
                    title: t('march20km.table.lastUpdated'),
                    dataIndex: 'lastUpdated',
                    key: 'lastUpdated',
                    render: (value: Date | null | undefined) => {
                        if (!value) {
                            return t('emptyValue');
                        }
                        return formatDateTimeWithSeconds(value);
                    },
                },
                {
                    title: t('march20km.table.lastActionBy'),
                    dataIndex: 'lastActionBy',
                    key: 'lastActionBy',
                },
                {
                    title: t('march20km.table.actions'),
                    dataIndex: 'actions',
                    key: 'actions',
                    className: 'text-right',
                    render: (_: any, record: March20kmPerformanceRowData) =>
                        abilities.canManagePerformanceResultsForCompany(selectedCompanyId) && (
                            <div className="flex justify-end space-x-1">
                                <IconButton
                                    onClick={() => handleEdit(record)}
                                    title={t('actions.editBtn')}
                                    icon={<Pencil className="h-4 w-4 text-yellow-500" strokeWidth={2} />}
                                />
                                <IconButton
                                    onClick={() => handleDelete(record)}
                                    title={t('actions.deleteBtn')}
                                    icon={<Trash2 className="h-4 w-4 text-red-500" strokeWidth={2} />}
                                />
                            </div>
                        ),
                },
            ],
            [
                t,
                getRoleLabel,
                formatDate,
                formatDateTimeWithSeconds,
                abilities,
                selectedCompanyId,
                handleEdit,
                handleDelete,
            ]
        );

        const dataSource: March20kmPerformanceRowData[] = useMemo(() => {
            if (data?.performanceResults?.items) {
                return data.performanceResults.items
                    .map((item: any) => {
                        if (item.result.__typename !== 'March20kmResult') {
                            return null;
                        }

                        return {
                            id: item.id,
                            traineeName: item.user?.name || t('emptyValue'),
                            assessmentDate: item.completedAt ? new Date(item.completedAt) : undefined,
                            timingSec: item.result.timingSec ?? undefined,
                            result: item.result.result ?? undefined,
                            assessmentRunId: item.assessmentRunId ?? undefined,
                            isTiedToAssessmentRun: item.isTiedToAssessmentRun ?? false,
                            assessmentRunName: item.assessmentRun?.name || t('emptyValue'),
                            lastActionBy: item.updatedByUser?.name || t('emptyValue'),
                            lastUpdated: item.updatedAt ? new Date(item.updatedAt) : undefined,
                        };
                    })
                    .filter(item => item !== null);
            }

            return [];
        }, [data, t]);

        const handlePageChange = useCallback(
            (newPage: number) => {
                dispatch({ type: 'SET_PAGE', page: newPage });
            },
            [dispatch]
        );

        const handlePageSizeChange = useCallback(
            (newPageSize: number) => {
                dispatch({ type: 'SET_PAGE_SIZE', pageSize: newPageSize });
            },
            [dispatch]
        );

        if (loading && !dataSource.length) {
            return <Loader />;
        }

        if ((!dataSource.length && !loading) || error) {
            return (
                <EmptyContainer>
                    <Empty
                        title={t('error.noRecordsFound')}
                        description={t('error.noRecordsFoundDesc')}
                        icon={FolderOpen}
                    />
                </EmptyContainer>
            );
        }

        return (
            <div>
                <Table<March20kmPerformanceRowData> columns={columns} dataSource={dataSource} rowKey="id" />

                {totalCount > 0 && (
                    <div className="mt-4">
                        <TableFooter
                            currentPage={page}
                            pageSize={pageSize}
                            totalCount={totalCount}
                            pageSizeOptions={pageSizeOptions}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                        />
                    </div>
                )}

                <EditPerformanceMarch20KmModal
                    key={openEditModal ? 'open' : 'close'}
                    open={openEditModal}
                    onClose={handleOnEditModalClose}
                    rowData={editData}
                    assessmentRunOptions={assessmentRunOptions}
                />
            </div>
        );
    }
);

March20kmPerformanceTab.displayName = 'March20kmPerformanceTab';

export default March20kmPerformanceTab;
